import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { Box, useTheme, useMediaQuery, CircularProgress, Fab } from '@mui/material';
import { Edit as ComposeIcon } from '@mui/icons-material';
import LeftIconBar from './LeftIconBar';
import MailSidebar from './MailSidebar';
import RightActionBar from './RightActionBar';
import MailHeader from './MailHeader';
import MobileDrawer from './MobileDrawer';
import ComposeTabBar from './ComposeTabBar';
import { useThemeStore } from '../../stores/themeStore';
import { AdvancedSearchPanel } from '../search';
import type { SearchFilters } from '../search';
import { useMailStore } from '../../stores/mailStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { useComposeTabsStore } from '../../stores/composeTabsStore';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import KeyboardShortcutsHelp from '../common/KeyboardShortcutsHelp';
const DomainGroupChat = lazy(() => import('../chat/DomainGroupChat'));
import { isHelpdeskMode } from '../../utils/helpdeskMode';

const ComposePage = lazy(() => import('../../pages/compose/ComposePage'));

const LEFT_ICON_BAR_WIDTH = 56;
const MOBILE_ICON_BAR_WIDTH = 44;
const MAIL_SIDEBAR_WIDTH = 200;
const RIGHT_ACTION_BAR_WIDTH = 44;
const HEADER_HEIGHT = 44;
const MOBILE_HEADER_HEIGHT = 48;

interface MailLayoutProps {
  hideOutlet?: boolean;
}

const MailLayout: React.FC<MailLayoutProps> = ({ hideOutlet = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600-899px
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md')); // < 900px
  const helpdesk = isHelpdeskMode();
  
  const { sidebarView, setMode: setThemeMode, setDensityMode: setThemeDensity } = useThemeStore();
  const { setSearchQuery, setSearchFilters, fetchEmails, searchQuery, folders } = useMailStore();
  const { settings, fetchSettings } = useSettingsStore();
  const { showingCompose } = useComposeTabsStore();
  const { createTab, setShowingCompose } = useComposeTabsStore();
  const isSidebarCollapsed = sidebarView === 'collapsed';
  
  // On mobile/tablet, always collapse sidebar and show drawer instead
  const effectiveSidebarWidth = helpdesk ? 0 : (isSmallScreen ? 0 : (isSidebarCollapsed ? 0 : MAIL_SIDEBAR_WIDTH));
  const effectiveIconBarWidth = isMobile ? MOBILE_ICON_BAR_WIDTH : LEFT_ICON_BAR_WIDTH;
  const showLeftIconBar = true; // Always show icon bar, including mobile
  const showRightActionBar = !isSmallScreen && !helpdesk;

  const [showAdvancedPanel, setShowAdvancedPanel] = useState(false);
  const [currentFilters, setCurrentFilters] = useState<SearchFilters>({});
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Enable keyboard shortcuts (respects user setting)
  useKeyboardShortcuts(settings.enableKeyboardShortcuts !== false);

  // Fetch user settings on mount - this ensures settings are loaded globally
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Sync theme settings with themeStore when settings are loaded
  useEffect(() => {
    if (settings.theme) {
      setThemeMode(settings.theme as 'light' | 'dark' | 'system');
    }
    if (settings.displayDensity) {
      setThemeDensity(settings.displayDensity as 'comfortable' | 'cozy' | 'compact');
    }
  }, [settings.theme, settings.displayDensity, setThemeMode, setThemeDensity]);

  const handleAdvancedSearch = (query: string, filters: SearchFilters) => {
    // Keep filter state in sync before query-triggered fetch to avoid stale requests.
    setSearchFilters(filters);
    setSearchQuery(query);
  };

  const handleToggleMobileDrawer = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const handleMobileCompose = () => {
    createTab();
    setShowingCompose(true);
  };

  return (
    <Box sx={{ display: 'flex', height: '100%', bgcolor: theme.palette.mode === 'dark' ? '#0c1222' : '#f5f5f5', overflow: 'hidden' }}>
      {/* Mobile Drawer - only on mobile/tablet */}
      {isSmallScreen && (
        <MobileDrawer 
          open={mobileDrawerOpen} 
          onClose={() => setMobileDrawerOpen(false)}
          folders={folders}
        />
      )}

      {/* Left Icon Navigation Bar - always visible, narrower on mobile */}
      {showLeftIconBar && <LeftIconBar width={effectiveIconBarWidth} isMobile={isMobile} />}

      {/* Mail Sidebar with folders - hidden on mobile/tablet and helpdesk mode */}
      {!isSmallScreen && !helpdesk && (
        <MailSidebar width={effectiveSidebarWidth} collapsed={isSidebarCollapsed} />
      )}

      {/* Main Content Area */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          // Dynamic margins based on screen size
          ml: isMobile 
            ? `${MOBILE_ICON_BAR_WIDTH}px` 
            : isTablet 
              ? `${LEFT_ICON_BAR_WIDTH}px` 
              : `${LEFT_ICON_BAR_WIDTH + effectiveSidebarWidth}px`,
          mr: isSmallScreen ? 0 : `${RIGHT_ACTION_BAR_WIDTH}px`,
          height: '100%',
          transition: 'margin 0.25s ease-in-out',
          overflow: 'hidden',
        }}
      >
        {/* Unified header bar - tabs + search + actions on one row (Zoho style) — hidden in helpdesk mode */}
        {!helpdesk && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: isMobile ? MOBILE_HEADER_HEIGHT : HEADER_HEIGHT,
              minHeight: isMobile ? MOBILE_HEADER_HEIGHT : HEADER_HEIGHT,
              bgcolor: theme.palette.mode === 'dark' ? '#111827' : '#ffffff',
              borderBottom: `1px solid ${theme.palette.mode === 'dark' ? '#1e293b' : '#e0e0e0'}`,
              flexShrink: 0,
            }}
          >
            <ComposeTabBar />
            <MailHeader 
              height={isMobile ? MOBILE_HEADER_HEIGHT : HEADER_HEIGHT} 
              showAdvancedPanel={showAdvancedPanel}
              onToggleAdvanced={setShowAdvancedPanel}
              isMobile={isMobile}
              onMenuClick={handleToggleMobileDrawer}
            />
          </Box>
        )}

        {/* Inline Advanced Search Panel - Below Header */}
        {!helpdesk && (
          <AdvancedSearchPanel
            open={showAdvancedPanel}
            onClose={() => setShowAdvancedPanel(false)}
            onSearch={handleAdvancedSearch}
            currentQuery={searchQuery}
            currentFilters={currentFilters}
            onFiltersChange={setCurrentFilters}
          />
        )}

        {/* Email Content Area */}
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: theme.palette.mode === 'dark' ? '#111827' : '#ffffff',
            borderRadius: isMobile ? 0 : '4px 4px 0 0',
            boxShadow: isMobile ? 'none' : (theme.palette.mode === 'dark' ? '0 -1px 4px rgba(0,0,0,0.3)' : '0 -1px 3px rgba(0,0,0,0.04)'),
            m: isMobile ? 0 : 0.25,
            mt: isMobile ? 0 : 0.25,
            border: theme.palette.mode === 'dark' ? '1px solid #1e293b' : '1px solid #e0e0e0',
            borderBottom: 'none',
          }}
        >
          {!hideOutlet && (
            <>
              {/* Mail view - hidden when compose is showing, but stays mounted */}
              <Box sx={{ display: showingCompose ? 'none' : 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }}>
                <Outlet />
              </Box>
              {/* Compose view - shown when a compose tab is active */}
              {showingCompose && (
                <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}><CircularProgress /></Box>}>
                  <ComposePage />
                </Suspense>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Right Action Bar - hidden on mobile */}
      {showRightActionBar && <RightActionBar width={RIGHT_ACTION_BAR_WIDTH} />}

      {/* Domain Group Chat — hidden in helpdesk mode */}
      {!helpdesk && <Suspense fallback={null}><DomainGroupChat /></Suspense>}

      {/* Keyboard Shortcuts Help Dialog */}
      <KeyboardShortcutsHelp />

      {/* Mobile Compose FAB */}
      {isMobile && !showingCompose && (
        <Fab
          color="primary"
          onClick={handleMobileCompose}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1200,
            width: 52,
            height: 52,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}
        >
          <ComposeIcon />
        </Fab>
      )}
    </Box>
  );
};

export default MailLayout;
